import { useState, useEffect } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { checkPhoneNumber } from 'parent-portal/public/PhoneNumberExists';
import { getUser, IAddressBody, isLoggedIn, patchUser, businessSignUp } from 'api/UserApi';
import useFamilyApi from 'api/FamilyApi';
import { consoleLogInDev, formatE164USPhoneNumber } from 'shared';
import useCookies from 'shared/Cookies';
import { useHistory } from 'react-router-dom';
import { useUserContext } from 'UserContext';
import { BusinessData } from './business/BusinessModels';
import usePublicSessionTracking, { PublicSessionEventType } from 'parent-portal/shared/PublicSessionTracking';
import useQueryParams from 'shared/QueryParamsHook';
import { UserContextUser } from 'models';

export type SectionKey = 'personal' | 'address' | 'account' | 'business' | 'review';

interface UseSignUpFormStateArgs {
    existingEmail?: string;
    existingPhoneNumber?: string;
    redirect?: boolean;
    isBusinessSignup: boolean;
    businessType: 'CHILD_CARE' | 'ELDERCARE';
    location?: string;
    onClose?: () => void;
    onComplete?: (user: UserContextUser) => void;
}

export default function useSignUpFormState({
    existingEmail,
    existingPhoneNumber,
    redirect = true,
    isBusinessSignup,
    businessType,
    location,
    onClose,
    onComplete,
}: UseSignUpFormStateArgs) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [display, setDisplay] = useState('');
    const [isValidPhone, setIsValidPhone] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const [isDuplicatePhone, setIsDuplicatePhone] = useState(false);
    const [phoneErrorText, setPhoneErrorText] = useState('');

    const [address, setAddress] = useState<IAddressBody>();
    const [isAddressValid, setIsAddressValid] = useState(false);

    const [email, setEmail] = useState(existingEmail ?? '');
    const [isEmail, setIsEmail] = useState(true);
    const [password, setPassword] = useState('');
    const [isPassword, setIsPassword] = useState(true);
    const [valid, setValid] = useState(false);

    const [businessData, setBusinessData] = useState<BusinessData>();
    const [businessError, setBusinessError] = useState(true);
    const [isValidProgramId, setIsValidProgramId] = useState(false);

    const [referralCode, setReferralCode] = useState('');
    const [referralCodeValid, setReferralCodeValid] = useState(true);
    const [consent, setConsent] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string>();
    const [buttonDisabled, setButtonDisabled] = useState(true);

    const [activeSection, setActiveSection] = useState<SectionKey>('personal');

    const history = useHistory();
    const { refreshUser, user } = useUserContext();
    const { getTrackingValues } = useFamilyApi();
    const {
        cookies: { puuid, isBusiness, referral, _fbp, _fbc },
    } = useCookies(['puuid', 'isBusiness', 'referral', '_fbp', '_fbc']);
    const { trackSession } = usePublicSessionTracking('Family Signup Modal');
    const query = useQueryParams();
    const fbclid = query.get('fbclid');

    const showBusiness = isBusinessSignup || isBusiness === 'true';
    const personalComplete = !!firstName && !!lastName && isValidPhone && !phoneError;
    const addressComplete = isAddressValid;
    const accountComplete = !!email && isEmail && !!password && isPassword && valid;
    const businessComplete = !businessError && !!businessData?.name && isValidProgramId;

    const sectionOrder: SectionKey[] = showBusiness
        ? ['personal', 'address', 'account', 'business', 'review']
        : ['personal', 'address', 'account', 'review'];

    const passwordErrorText =
        email === password
            ? 'Email and password cannot match'
            : 'Password must be minimum 8 characters with at least one uppercase letter, one lowercase letter, one digit, and one special character.';

    function goToNextFrom(current: SectionKey) {
        const currentIndex = sectionOrder.indexOf(current);
        if (currentIndex < sectionOrder.length - 1) {
            setActiveSection(sectionOrder[currentIndex + 1]);
        }
    }

    function toggleSection(key: SectionKey) {
        setActiveSection((prev) => (prev === key ? (null as unknown as SectionKey) : key));
    }

    useEffect(() => {
        if (existingEmail) setEmail(existingEmail);
    }, [existingEmail]);

    useEffect(() => {
        if (existingPhoneNumber) setPhoneNumber(existingPhoneNumber);
        else if (user?.phone_number) setPhoneNumber(user.phone_number);
    }, [existingPhoneNumber, user?.phone_number]);

    useEffect(() => {
        setIsValidProgramId(
            address?.state !== 'Ohio' && address?.state !== 'Illinois'
                ? true
                : !!businessData?.programId &&
                      !!businessData?.programId?.length &&
                      businessData?.programId?.length > 5 &&
                      businessData?.programId?.length < 21,
        );
    }, [businessData?.programId, address?.state]);

    useEffect(() => {
        const genericSignupDisabled =
            !valid ||
            !consent ||
            !firstName ||
            !lastName ||
            !isAddressValid ||
            !isValidPhone ||
            !isEmail ||
            !password ||
            !isPassword ||
            !isValidProgramId;

        if (isBusinessSignup) {
            const { referral_code } = getTrackingValues();
            setReferralCode(referral_code);
            setButtonDisabled(genericSignupDisabled || businessError);
        } else {
            setButtonDisabled(genericSignupDisabled);
        }
    }, [
        consent,
        firstName,
        lastName,
        address,
        email,
        referralCodeValid,
        isValidPhone,
        isDuplicatePhone,
        isEmail,
        password,
        isPassword,
        valid,
        businessError,
    ]);

    useEffect(() => {
        setIsValidPhone(isValidPhoneNumber(phoneNumber, 'US'));
        const err = !isValidPhone && !!phoneNumber;
        setPhoneError(err);
        setPhoneErrorText(err ? 'Please enter a valid phone number' : '');
    }, [phoneNumber, isValidPhone]);

    useEffect(() => {
        async function loadUser() {
            const res = await getUser();
            if (res.signup_page > 1 && redirect === true) {
                history.push('/home');
            }
        }
        if (isLoggedIn()) loadUser();
    }, []);

    async function checkIsDuplicatePhone() {
        const result = await checkPhoneNumber(phoneNumber);
        setIsDuplicatePhone(result.duplicate);
        if (result.duplicate) {
            setPhoneError(true);
            setPhoneErrorText(
                'Please confirm that your phone number or email is not already linked to a Tandem account.',
            );
        }
        return result;
    }

    function isFormValid() {
        return (
            firstName &&
            lastName &&
            email &&
            password &&
            phoneNumber &&
            address?.street &&
            address?.city &&
            address.state &&
            address.zip_code &&
            isValidProgramId
        );
    }

    async function handlePostUser() {
        setIsSubmitting(true);
        const result = await checkIsDuplicatePhone();
        if (address && isFormValid() && businessData?.name && !result.duplicate) {
            setError(undefined);
            const { source, ciid, esid, reid } = getTrackingValues();

            const postBody = {
                username: email,
                email,
                password1: password,
                password2: password,
                first_name: firstName,
                last_name: lastName,
                phone_number: formatE164USPhoneNumber(phoneNumber) as string,
                street: address.street!,
                city: address.city!,
                state: address.state!,
                zip_code: address.zip_code!,
                business_name: businessData?.name,
                business_type: businessType,
                program_id: businessData?.programId,
                program_types: businessData?.programTypes,
                location_types: businessData?.locationTypes,
                public_user_token: puuid,
                referral_code: referralCode,
                source,
                ciid,
                esid,
                reid,
                frontend_url: window.location.href,
                fbc: fbclid ?? _fbc,
                fbp: _fbp,
            };

            businessSignUp(postBody)
                .then((response) => {
                    if (response) localStorage.setItem('token', response.key);
                    return refreshUser();
                })
                .then((user) => {
                    onClose && onClose();
                    onComplete && onComplete(user);
                    setIsSubmitting(false);
                })
                .catch((error) => {
                    const errorFields = [
                        'username',
                        'email',
                        'password1',
                        'password2',
                        'first_name',
                        'last_name',
                        'phone_number',
                        'street',
                        'city',
                        'state',
                        'zip_code',
                        'business_name',
                        'business_type',
                        'program_id',
                        'non_field_errors',
                        'frontend_url',
                        'fbc',
                        'fbp',
                    ];

                    const allErrors = errorFields.reduce((errorsAccumulator: string[], field) => {
                        // eslint-disable-next-line security/detect-object-injection
                        return error[field] ? [...errorsAccumulator, error[field]] : errorsAccumulator;
                    }, []);

                    if (allErrors.length) {
                        setError(`The following errors occurred: ${allErrors.join(' ')}`);
                    } else {
                        setError('An unexpected error occurred. Please try again.');
                    }

                    consoleLogInDev(error);
                    setIsSubmitting(false);
                });
        } else {
            setIsSubmitting(false);
        }
        trackSession('CLICK', `Family signup modal submitted`, PublicSessionEventType.User, {
            firstName,
            lastName,
            email,
            phoneNumber,
            phoneErrorText,
        });
    }

    async function finallyComplete(token: string) {
        if (token) localStorage.setItem('token', token);
        let user = await getUser(token);
        user = await patchUser(user.id, { signup_page: 4 }, location || '', token);
        user = await refreshUser();
        onClose && onClose();
        onComplete && onComplete(user);
        setIsSubmitting(false);
    }

    return {
        firstName,
        setFirstName,
        lastName,
        setLastName,
        phoneNumber,
        display,
        setPhoneNumber,
        setDisplay,
        phoneError,
        phoneErrorText,

        address,
        setAddress,
        setIsAddressValid,

        email,
        isEmail,
        setIsEmail,
        setEmail,
        setValid,
        password,
        isPassword,
        setPassword,
        setIsPassword,
        passwordErrorText,

        businessData,
        setBusinessData,
        businessError,
        setBusinessError,
        isValidProgramId,

        referral,
        referralCode,
        setReferralCode,
        referralCodeValid,
        setReferralCodeValid,
        consent,
        setConsent,

        isSubmitting,
        error,
        buttonDisabled,
        handlePostUser,
        finallyComplete,

        showBusiness,
        personalComplete,
        addressComplete,
        accountComplete,
        businessComplete,
        activeSection,
        goToNextFrom,
        toggleSection,

        user,
    };
}
